import {
  NAKSHATRAS,
  RASHI_ENGLISH,
  type Placement,
} from './zodiac.ts';
import {
  ASHTAKOOT_CONVENTION,
  DISPUTED_NOTES,
  GANA_MATRIX,
  GRAHA_MAITRI_SCORE,
  NAKSHATRA_ATTRS,
  SIGN_ATTRS,
  SIGN_LORDS,
  VARNA_RANK,
  VASHYA_MATRIX,
  YONI_MATRIX,
  YONI_ORDER,
  vashyaClassOf,
  type Gana,
  type VashyaClass,
} from './tables/ashtakoot.ts';

/**
 * Ashtakoot (Gun Milan) scoring — the engine behind the Kundli Matching
 * tool.
 *
 * ── Why this file is pure arithmetic ───────────────────────────────────────
 * Every doctrinal fact lives in tables/ashtakoot.ts, which is research-
 * verified cell by cell and snapshot-frozen pending the practitioner's
 * sign-off (HANDOFF.md §9: a single wrong cell produces a plausible
 * 36-point total people make marriage decisions on). This file only walks
 * those tables, so a doctrine correction never touches scoring logic.
 *
 * ── The two decided behaviours (HANDOFF.md §9) ─────────────────────────────
 * 1. No marry / don't-marry verdict. The result is the score, what each
 *    kuta measures, and the working — never a judgement.
 * 2. Doshas are reported, never auto-cancelled. Where a classical
 *    cancellation condition holds, we SAY that authorities differ and show
 *    the dosha anyway.
 *
 * Direction convention: every two-dimensional table in tables/ashtakoot.ts
 * is BRIDE-first — MATRIX[bride][groom] — matching the classical
 * tabulations (Gana and Vashya are directional; Varna compares the bride's
 * varna against the groom's).
 */

export interface KutaResult {
  key: string;
  name: string;
  measures: string;
  max: number;
  score: number;
  working: string;
  /** Set when published authorities disagree about this cell or rule. */
  disputed?: string;
}

export interface DoshaResult {
  name: string;
  present: boolean;
  note: string;
}

export interface MatchResult {
  kutas: KutaResult[];
  total: number;
  max: 36;
  doshas: DoshaResult[];
  convention: string;
}

/** Inclusive count from nakshatra a to b, 1-based, wrapping at 27. */
function countNakshatras(from: number, to: number): number {
  return ((to - from + 27) % 27) + 1;
}

function varnaKuta(bride: Placement, groom: Placement): KutaResult {
  const bv = SIGN_ATTRS[bride.rashiIndex]!.varna;
  const gv = SIGN_ATTRS[groom.rashiIndex]!.varna;
  const score = VARNA_RANK[gv] >= VARNA_RANK[bv] ? 1 : 0;
  return {
    key: 'varna',
    name: 'Varna',
    measures: 'Temperamental grade of the two Moon signs',
    max: 1,
    score,
    working: `Bride's sign ${RASHI_ENGLISH[bride.rashiIndex]} is ${bv}; groom's sign ${RASHI_ENGLISH[groom.rashiIndex]} is ${gv}. The point is given when the groom's varna is the same or higher in the traditional order.`,
    disputed: DISPUTED_NOTES.varna,
  };
}

function vashyaKuta(bride: Placement, groom: Placement): KutaResult {
  const bc: VashyaClass = vashyaClassOf(bride.rashiIndex, bride.degreeInRashi);
  const gc: VashyaClass = vashyaClassOf(groom.rashiIndex, groom.degreeInRashi);
  const score = VASHYA_MATRIX[bc][gc];
  return {
    key: 'vashya',
    name: 'Vashya',
    measures: 'Mutual influence between the sign groups',
    max: 2,
    score,
    working: `Groom's group ${gc}, bride's group ${bc} → ${score} of 2 by the standard table.`,
    disputed: DISPUTED_NOTES.vashya,
  };
}

function taraKuta(bride: Placement, groom: Placement): KutaResult {
  const INAUSPICIOUS = new Set([3, 5, 7]);
  const fromBride = countNakshatras(bride.nakshatraIndex, groom.nakshatraIndex) % 9 || 9;
  const fromGroom = countNakshatras(groom.nakshatraIndex, bride.nakshatraIndex) % 9 || 9;
  const badB = INAUSPICIOUS.has(fromBride);
  const badG = INAUSPICIOUS.has(fromGroom);
  const score = badB && badG ? 0 : badB || badG ? 1.5 : 3;
  return {
    key: 'tara',
    name: 'Tara',
    measures: 'Birth-star compatibility, counted both ways',
    max: 3,
    score,
    working: `Counting ${bride.nakshatra} → ${groom.nakshatra} gives tara ${fromBride}; the reverse gives ${fromGroom}. Taras 3, 5 and 7 are the difficult ones; ${badB || badG ? (badB && badG ? 'both counts land there' : 'one count lands there') : 'neither count lands there'}.`,
  };
}

function yoniKuta(bride: Placement, groom: Placement): KutaResult {
  const by = NAKSHATRA_ATTRS[bride.nakshatraIndex]!.yoni;
  const gy = NAKSHATRA_ATTRS[groom.nakshatraIndex]!.yoni;
  const bi = YONI_ORDER.indexOf(by);
  const gi = YONI_ORDER.indexOf(gy);
  const score = YONI_MATRIX[bi]![gi]!;
  return {
    key: 'yoni',
    name: 'Yoni',
    measures: 'Instinctive compatibility, by nakshatra animal',
    max: 4,
    score,
    working: `${bride.nakshatra} carries the ${by} yoni; ${groom.nakshatra} carries the ${gy} yoni → ${score} of 4.`,
    disputed: DISPUTED_NOTES.yoni,
  };
}

function maitriKuta(bride: Placement, groom: Placement): KutaResult {
  const bl = SIGN_LORDS[bride.rashiIndex]!;
  const gl = SIGN_LORDS[groom.rashiIndex]!;
  const score = GRAHA_MAITRI_SCORE(bl, gl);
  return {
    key: 'maitri',
    name: 'Graha Maitri',
    measures: 'Friendship between the Moon-sign lords',
    max: 5,
    score,
    working: `Bride's sign lord is ${bl}; groom's is ${gl}. Scored from Parashara's permanent friendship table.`,
    disputed: DISPUTED_NOTES.maitri,
  };
}

function ganaKuta(bride: Placement, groom: Placement): KutaResult {
  const bg: Gana = NAKSHATRA_ATTRS[bride.nakshatraIndex]!.gana;
  const gg: Gana = NAKSHATRA_ATTRS[groom.nakshatraIndex]!.gana;
  const score = GANA_MATRIX[bg][gg];
  return {
    key: 'gana',
    name: 'Gana',
    measures: 'Temperament group (deva / manushya / rakshasa)',
    max: 6,
    score,
    working: `Bride's nakshatra is ${bg} gana; groom's is ${gg} gana → ${score} of 6. This table is asymmetric by tradition: the same pair scores differently with the roles reversed.`,
    disputed: DISPUTED_NOTES.gana,
  };
}

function bhakootKuta(bride: Placement, groom: Placement): { kuta: KutaResult; dosha: DoshaResult } {
  /* Sign distance counted inclusively both ways; the classical bad pairs
     are 2/12, 5/9 and 6/8. */
  const d1 = ((groom.rashiIndex - bride.rashiIndex + 12) % 12) + 1;
  const d2 = ((bride.rashiIndex - groom.rashiIndex + 12) % 12) + 1;
  const pair = [Math.min(d1, d2), Math.max(d1, d2)].join('/');
  const bad = pair === '2/12' || pair === '5/9' || pair === '6/8';
  const score = bad ? 0 : 7;
  return {
    kuta: {
      key: 'bhakoot',
      name: 'Bhakoot',
      measures: 'Relative position of the two Moon signs',
      max: 7,
      score,
      working: `${RASHI_ENGLISH[bride.rashiIndex]} and ${RASHI_ENGLISH[groom.rashiIndex]} sit ${pair} from each other. The 2/12, 5/9 and 6/8 pairs score 0; everything else scores the full 7.`,
    },
    dosha: {
      name: 'Bhakoot dosha',
      present: bad,
      note: bad
        ? `The signs form the ${pair} pair, which tradition treats as Bhakoot dosha. Some authorities cancel it when the sign lords are friends — authorities differ, so we report it either way and leave the judgement to a reading.`
        : 'Not present.',
    },
  };
}

function nadiKuta(bride: Placement, groom: Placement): { kuta: KutaResult; dosha: DoshaResult } {
  const bn = NAKSHATRA_ATTRS[bride.nakshatraIndex]!.nadi;
  const gn = NAKSHATRA_ATTRS[groom.nakshatraIndex]!.nadi;
  const same = bn === gn;
  const score = same ? 0 : 8;
  return {
    kuta: {
      key: 'nadi',
      name: 'Nadi',
      measures: 'Constitutional type (adi / madhya / antya)',
      max: 8,
      score,
      working: `${bride.nakshatra} is ${bn} nadi; ${groom.nakshatra} is ${gn} nadi — ${same ? 'the same, which scores 0' : 'different, which scores the full 8'}.`,
    },
    dosha: {
      name: 'Nadi dosha',
      present: same,
      note: same
        ? `Both Moons sit in the ${bn} nadi. Classical texts weigh this dosha heavily; several authorities cancel it when sign or nakshatra conditions hold — authorities differ, so we report it either way and leave the judgement to a reading.`
        : 'Not present.',
    },
  };
}

/** The full Ashtakoot match from two sidereal Moon placements. */
export function matchKundli(bride: Placement, groom: Placement): MatchResult {
  const bhakoot = bhakootKuta(bride, groom);
  const nadi = nadiKuta(bride, groom);
  const kutas: KutaResult[] = [
    varnaKuta(bride, groom),
    vashyaKuta(bride, groom),
    taraKuta(bride, groom),
    yoniKuta(bride, groom),
    maitriKuta(bride, groom),
    ganaKuta(bride, groom),
    bhakoot.kuta,
    nadi.kuta,
  ];
  const total = Math.round(kutas.reduce((s, k) => s + k.score, 0) * 2) / 2;
  return {
    kutas,
    total,
    max: 36,
    doshas: [bhakoot.dosha, nadi.dosha],
    convention: ASHTAKOOT_CONVENTION,
  };
}

/** Exposed for tests. */
export const _internal = { countNakshatras };
export { NAKSHATRAS };
